import { defineStore } from 'pinia'
import type { IMessage } from 'src/interfaces/common'
import { computed, ref } from 'vue'
import { useLoader } from 'src/hooks/useLoader'
import { LOADERS } from 'src/config/constants'
import {
  getUnreadCounts,
  getAnnouncements,
  markAllAnnouncementsRead,
  type UnreadCountsResponse,
  type AnnouncementModel,
} from 'src/service/messaging'

export const useClientMessagingStore = defineStore('client.messaging', () => {
  const isInitialzed = ref(false)
  const messages = ref<IMessage[]>([])
  const parentPortalUnreadMessages = ref<Record<string, number>>({})
  const totalUnreadCount = ref(0)
  const useNewMessaging = ref(false)
  const isMessengerOpen = ref(false)
  const pendingUnreadByConversation = ref<Record<string, number>>({})

  // Active tab in messenger
  type TabKey = 'room' | 'admin' | 'announcements'
  const activeTab = ref<TabKey>('room')

  // Client announcements state
  const announcements = ref<AnnouncementModel[]>([])
  const isLoadingAnnouncements = ref(false)
  const isLoadingMoreAnnouncements = ref(false)
  const announcementsContinuationToken = ref<string | null>(null)
  const hasMoreAnnouncements = computed(
    () => !!announcementsContinuationToken.value,
  )

  // Detailed unread counts for messenger tab badges
  const unreadCountBreakdown = ref<UnreadCountsResponse>({
    total: 0,
    roomThreads: 0,
    adminThreads: 0,
  })

  const unreadMessagesCount = computed<number>(() => {
    if (useNewMessaging.value) {
      return totalUnreadCount.value
    }
    if (!parentPortalUnreadMessages.value) return 0
    return parentPortalUnreadMessages.value['Center'] || 0
  })

  const setMessages = (newMessages: IMessage[]) => {
    messages.value = newMessages
  }

  const setUnreadMessages = (unreadMessages: Record<string, number>) => {
    parentPortalUnreadMessages.value = unreadMessages
  }

  const clearUnreadMessages = () => {
    parentPortalUnreadMessages.value = {}
    totalUnreadCount.value = 0
    unreadCountBreakdown.value = { total: 0, roomThreads: 0, adminThreads: 0 }
    announcements.value = []
    announcementsContinuationToken.value = null
    activeTab.value = 'room'
  }

  const setIsInitialized = (initialized: boolean) => {
    isInitialzed.value = initialized
  }

  const setUnreadCountBreakdown = (counts: UnreadCountsResponse) => {
    unreadCountBreakdown.value = counts
    totalUnreadCount.value = counts.total
  }

  const clearTabUnread = (
    tab: 'roomThreads' | 'adminThreads' | 'announcements',
  ) => {
    const current = unreadCountBreakdown.value
    const delta = current[tab] ?? 0
    if (delta <= 0) return
    unreadCountBreakdown.value = {
      ...current,
      [tab]: 0,
      total: Math.max(0, current.total - delta),
    }
    totalUnreadCount.value = unreadCountBreakdown.value.total
  }

  const incrementTabUnread = (
    tab: 'roomThreads' | 'adminThreads' | 'announcements',
  ) => {
    const current = unreadCountBreakdown.value
    unreadCountBreakdown.value = {
      ...current,
      [tab]: (current[tab] ?? 0) + 1,
      total: current.total + 1,
    }
    totalUnreadCount.value = unreadCountBreakdown.value.total
  }

  const refreshUnreadCounts = async () => {
    try {
      const response = await getUnreadCounts()
      setUnreadCountBreakdown(response)
    } catch {
      // Silently fail — badge will just show 0
    }
  }

  const fetchAnnouncements = async () => {
    isLoadingAnnouncements.value = true
    try {
      const response = await getAnnouncements(20)
      announcements.value = [...(response.pinnedItems ?? []), ...(response.items || [])]
      announcementsContinuationToken.value = response.continuationToken ?? null
    } catch {
      announcements.value = []
      announcementsContinuationToken.value = null
    } finally {
      isLoadingAnnouncements.value = false
    }
  }

  const fetchMoreAnnouncements = async () => {
    if (
      !announcementsContinuationToken.value ||
      isLoadingMoreAnnouncements.value
    )
      return
    isLoadingMoreAnnouncements.value = true
    try {
      const response = await getAnnouncements(
        20,
        announcementsContinuationToken.value,
      )
      announcements.value = [...announcements.value, ...(response.items || [])]
      announcementsContinuationToken.value = response.continuationToken ?? null
    } catch {
      // keep existing announcements on error
    } finally {
      isLoadingMoreAnnouncements.value = false
    }
  }

  const markAnnouncementsRead = async () => {
    clearTabUnread('announcements')
    void markAllAnnouncementsRead().catch(() => {})
  }

  const handleNewAnnouncement = async () => {
    if (isMessengerOpen.value && activeTab.value === 'announcements') {
      await fetchAnnouncements()
      void markAllAnnouncementsRead().catch(() => {})
    } else {
      incrementTabUnread('announcements')
    }
  }

  const initializeNew = async () => {
    if (isInitialzed.value) return
    useNewMessaging.value = true

    try {
      const response = await getUnreadCounts()
      setUnreadCountBreakdown(response)
      isInitialzed.value = true
    } catch {
      // Don't set initialized to true on error, allow retry
    }
  }

  const initializeLegacy = async () => {
    if (isInitialzed.value) return
    useNewMessaging.value = false

    const { useFetchLoader: fetchParentPortalUnreadMessagesCount } = useLoader<
      Record<string, number>
    >(LOADERS.per, {
      uri: 'parentPortalUnreadMessagesCount',
    })

    try {
      const result = await fetchParentPortalUnreadMessagesCount()
      if (result) {
        parentPortalUnreadMessages.value = result
      }
      isInitialzed.value = true
    } catch {
      // Don't set initialized to true on error, allow retry
    }
  }

  const initialize = async () => {
    if (useNewMessaging.value) {
      await initializeNew()
    } else {
      await initializeLegacy()
    }
  }

  return {
    isInitialzed,
    messages,
    unreadMessagesCount,
    parentPortalUnreadMessages,
    totalUnreadCount,
    useNewMessaging,
    isMessengerOpen,
    unreadCountBreakdown,
    setMessages,
    setUnreadMessages,
    clearUnreadMessages,
    setIsInitialized,
    setUnreadCountBreakdown,
    clearTabUnread,
    incrementTabUnread,
    initialize,
    initializeNew,
    initializeLegacy,
    refreshUnreadCounts,
    pendingUnreadByConversation,
    activeTab,
    announcements,
    isLoadingAnnouncements,
    isLoadingMoreAnnouncements,
    hasMoreAnnouncements,
    fetchAnnouncements,
    fetchMoreAnnouncements,
    markAnnouncementsRead,
    handleNewAnnouncement,
  }
})
